import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Loader2, ArrowLeft, AlertCircle, Copy, Check, X, ImagePlus } from "lucide-react";
import api, { formatDetail } from "@/lib/api";

/**
 * Tawk.to-style embed widget customizer for a single payment link.
 *
 * Layout: left = form, right = sticky live preview that re-renders on
 * every keystroke. The preview is a real DOM mock of the floating pill
 * (same inline styles the embed.js will produce) so what the merchant
 * sees here is precisely what their customers will see — including the
 * cosmetic & behaviour gates (balance threshold simulation, position).
 */
export default function EmbedCustomizer() {
    const { linkId } = useParams();
    const [link, setLink] = useState(null);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);
    const [snippet, setSnippet] = useState(null);
    const [copied, setCopied] = useState(false);
    // Simulated host-side context for the preview: lets the merchant
    // pretend they've already integrated and see what their customer
    // experience looks like at various balance levels.
    const [simBalance, setSimBalance] = useState(50);

    const [form, setForm] = useState({
        embed_enabled: true,
        embed_floating_mode: "bottom-right",
        embed_variant: "labeled",
        embed_iframe_width: 480,
        embed_iframe_height: 780,
        embed_bg_color: "#0f172a",
        embed_text_color: "#ffffff",
        embed_header_color: "",          // empty = inherit bg
        embed_header_text_color: "",     // empty = inherit text
        embed_accent_color: "",          // empty = inherit bg
        embed_label: "Pay Now",
        embed_border_radius_px: 999,
        embed_logo_url: "",
        embed_sticker: "none",
        embed_style: "solid",
        embed_size: "default",
        embed_balance_gating: false,
        embed_balance_threshold: 100,
        embed_amount_mode: "topup",
        embed_suggested_fixed: 500,
        embed_open_mode: "iframe",
        embed_allowed_origins: [],  // list of strings
        embed_auto_close_on_success: true,
        // Attention Grabber
        embed_grabber_enabled: false,
        embed_grabber_preset: "none",
        embed_grabber_image_url: "",
        embed_match_bubble_theme: false,
        // Mobile overrides
        embed_mobile_variant: "inherit",
        embed_mobile_position: "inherit",
    });
    const [grabberModalOpen, setGrabberModalOpen] = useState(false);
    const [originDraft, setOriginDraft] = useState("");

    useEffect(() => {
        (async () => {
            try {
                const [r1, r2] = await Promise.all([
                    api.get(`/partner/payment-links`),
                    api.get(`/public/embed-snippet/`).catch(() => null),
                ]);
                const all = r1.data.items || [];
                const me = all.find((l) => l.link_id === linkId);
                if (!me) { setErr("Payment link not found"); return; }
                setLink(me);
                setForm((f) => ({
                    ...f,
                    embed_enabled: me.embed_enabled !== false,
                    embed_floating_mode: me.embed_floating_mode || "bottom-right",
                    embed_variant: me.embed_variant || "labeled",
                    embed_iframe_width: me.embed_iframe_width ?? 480,
                    embed_iframe_height: me.embed_iframe_height ?? 780,
                    embed_bg_color: me.embed_bg_color || "#0f172a",
                    embed_text_color: me.embed_text_color || "#ffffff",
                    embed_header_color: me.embed_header_color || "",
                    embed_header_text_color: me.embed_header_text_color || "",
                    embed_accent_color: me.embed_accent_color || "",
                    embed_label: me.embed_label || "Pay Now",
                    embed_border_radius_px: me.embed_border_radius_px ?? 999,
                    embed_logo_url: me.embed_logo_url || "",
                    embed_sticker: me.embed_sticker || "none",
                    embed_style: me.embed_style || "solid",
                    embed_size: me.embed_size || "default",
                    embed_balance_gating: me.embed_balance_gating === true,
                    embed_balance_threshold: me.embed_balance_threshold ?? 100,
                    embed_amount_mode: me.embed_amount_mode || "topup",
                    embed_suggested_fixed: me.embed_suggested_fixed ?? 500,
                    embed_open_mode: me.embed_open_mode || "iframe",
                    embed_allowed_origins: me.embed_allowed_origins || [],
                    embed_auto_close_on_success: me.embed_auto_close_on_success !== false,
                    embed_grabber_enabled: me.embed_grabber_enabled === true,
                    embed_grabber_preset: me.embed_grabber_preset || "none",
                    embed_grabber_image_url: me.embed_grabber_image_url || "",
                    embed_match_bubble_theme: me.embed_match_bubble_theme === true,
                    embed_mobile_variant: me.embed_mobile_variant || "inherit",
                    embed_mobile_position: me.embed_mobile_position || "inherit",
                }));
                // Fetch the snippet text (host name + script tag).
                try {
                    const s = await api.get(`/public/embed-snippet/${me.link_code}`);
                    setSnippet(s.data);
                } catch (e) { console.warn("embed snippet fetch failed:", e); /* non-fatal */ }
            } catch (e) { setErr(formatDetail(e)); }
        })();
    }, [linkId]);

    const save = async () => {
        setBusy(true);
        setErr(null);
        try {
            await api.patch(`/partner/payment-links/${linkId}`, form);
        } catch (e) { setErr(formatDetail(e)); }
        finally { setBusy(false); }
    };

    const addOrigin = () => {
        const v = originDraft.trim();
        if (!v) return;
        if ((form.embed_allowed_origins || []).includes(v)) { setOriginDraft(""); return; }
        setForm({ ...form, embed_allowed_origins: [...(form.embed_allowed_origins || []), v] });
        setOriginDraft("");
    };

    if (err && !link) return (
        <div className="p-6 text-rose-600 text-sm flex items-center gap-2">
            <AlertCircle className="w-4 h-4" />{err}
        </div>
    );
    if (!link) return <div className="p-6 text-slate-400 text-sm"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</div>;

    return (
        <div className="space-y-4 max-w-6xl">
            <div className="flex items-center gap-2 text-sm">
                <Link to="/partner/links" className="text-slate-500 hover:text-slate-700 inline-flex items-center gap-1"><ArrowLeft className="w-3.5 h-3.5" />Payment links</Link>
                <span className="text-slate-300">/</span>
                <span className="font-semibold text-slate-900">{link.display_name || link.link_code}</span>
                <span className="text-slate-400 text-xs ml-1">· Embed widget</span>
            </div>

            <div className="grid lg:grid-cols-2 gap-5">
                {/* ── LEFT: Form ─────────────────────────────────── */}
                <div className="space-y-4">
                    <Section title="Desktop Widget" subtitle="Pick the pill style, iframe modal size, and where it lives on the page.">
                        <Toggle label="Enable embed widget"
                                hint="When OFF, the embed.js fetch returns 403 and the floating pill never renders. Manual data-paygate buttons also stop working."
                                checked={form.embed_enabled} onChange={(v) => setForm({ ...form, embed_enabled: v })} testid="embed-enabled" />
                        <Field label="Pill style" hint="“Labeled” shows sticker + text (CTA pill). “Bubble” shows a 56-px chat-style circle — sticker only, no text.">
                            <VariantPicker
                                value={form.embed_variant}
                                onChange={(v) => setForm({ ...form, embed_variant: v })}
                                bg={form.embed_bg_color} fg={form.embed_text_color}
                                label={form.embed_label}
                                testidPrefix="embed-variant"
                            />
                        </Field>
                        <div className="grid grid-cols-2 gap-2">
                            <Field label="Iframe width (px)" hint="Min 320, max 1280">
                                <input type="number" min={320} max={1280}
                                       data-testid="embed-iframe-width"
                                       value={form.embed_iframe_width ?? 480}
                                       onChange={(e) => setForm({ ...form, embed_iframe_width: Math.max(320, Math.min(1280, parseInt(e.target.value || "480", 10))) })}
                                       className="input" />
                            </Field>
                            <Field label="Iframe height (px)" hint="Min 400, max 1200">
                                <input type="number" min={400} max={1200}
                                       data-testid="embed-iframe-height"
                                       value={form.embed_iframe_height ?? 780}
                                       onChange={(e) => setForm({ ...form, embed_iframe_height: Math.max(400, Math.min(1200, parseInt(e.target.value || "780", 10))) })}
                                       className="input" />
                            </Field>
                        </div>
                        <Field label="Desktop widget position" hint="Click the cell where you want the pill anchored on the host page.">
                            <PositionPicker
                                value={form.embed_floating_mode}
                                onChange={(v) => setForm({ ...form, embed_floating_mode: v })}
                                includeOff={true}
                                testidPrefix="embed-position"
                            />
                        </Field>
                        <Toggle
                            label="Only show when balance is low"
                            hint="OFF (recommended) — pill always shows. ON — pill renders only when the host site supplies a balance via window.PayGate.context and that balance is below the threshold."
                            checked={form.embed_balance_gating}
                            onChange={(v) => setForm({ ...form, embed_balance_gating: v })}
                            testid="embed-balance-gating"
                        />
                        {form.embed_balance_gating && (
                            <Field label="Balance threshold" hint="Pill shows only when host-supplied balance is below this number. Requires the host to set window.PayGate.context = { balance: N }.">
                                <input type="number" min={0} value={form.embed_balance_threshold ?? ""}
                                       data-testid="embed-threshold"
                                       onChange={(e) => setForm({ ...form, embed_balance_threshold: e.target.value === "" ? null : parseFloat(e.target.value) })}
                                       className="input" placeholder="e.g. 100" />
                            </Field>
                        )}
                    </Section>

                    <Section title="Attention Grabber" subtitle="A rich illustrated card that floats above the pill on first impression — like Tawk.to's “We Are Here!” bubble. Picks the customer's attention without being pushy: dismisses with one tap and stays dismissed for the session.">
                        <Toggle
                            label="Enable attention grabber"
                            hint="When ON, the card renders ABOVE the pill until the customer dismisses it (one session). Pick a curated illustration below or upload a custom image."
                            checked={form.embed_grabber_enabled}
                            onChange={(v) => setForm({ ...form, embed_grabber_enabled: v })}
                            testid="embed-grabber-enabled"
                        />
                        {form.embed_grabber_enabled && (
                            <>
                                <Field label="Illustration" hint="Browse the gallery or paste a custom HTTPS / data: URL.">
                                    <div className="flex gap-2 items-center">
                                        <button type="button"
                                            data-testid="embed-grabber-open-gallery"
                                            onClick={() => setGrabberModalOpen(true)}
                                            className="px-3 py-1.5 rounded-md text-xs font-semibold bg-emerald-600 text-white hover:bg-emerald-700 inline-flex items-center gap-1.5">
                                            <ImagePlus className="w-3.5 h-3.5" />Open Gallery
                                        </button>
                                        {(form.embed_grabber_image_url || form.embed_grabber_preset !== "none") && (
                                            <button type="button"
                                                data-testid="embed-grabber-clear"
                                                onClick={() => setForm({ ...form, embed_grabber_image_url: "", embed_grabber_preset: "none" })}
                                                className="text-[11px] text-slate-500 hover:text-rose-600 underline">
                                                Clear selection
                                            </button>
                                        )}
                                    </div>
                                </Field>
                                <Field label="Custom image URL (overrides preset)" hint="https://example.com/your-banner.png or a base64 data: URL. Recommended: 180×130 px, < 100 KB.">
                                    <input data-testid="embed-grabber-image-url"
                                           value={form.embed_grabber_image_url || ""}
                                           onChange={(e) => setForm({ ...form, embed_grabber_image_url: e.target.value })}
                                           className="input" placeholder="https://…" />
                                </Field>
                                <Toggle
                                    label="Match widget with bubble theme"
                                    hint="Tints the grabber's backdrop with the pill's background colour for a unified look. Default OFF = white backdrop."
                                    checked={form.embed_match_bubble_theme}
                                    onChange={(v) => setForm({ ...form, embed_match_bubble_theme: v })}
                                    testid="embed-match-theme"
                                />
                            </>
                        )}
                    </Section>

                    <Section title="Mobile Widget" subtitle="Override position and pill style on small screens (≤768 px). Leave as “Same as desktop” to keep one config.">
                        <Field label="Mobile pill style">
                            <div className="flex gap-1.5" data-testid="embed-mobile-variant-picker">
                                {["inherit", "labeled", "bubble"].map((v) => (
                                    <button key={v} type="button"
                                        data-testid={`embed-mobile-variant-${v}`}
                                        onClick={() => setForm({ ...form, embed_mobile_variant: v })}
                                        className={`flex-1 px-2 py-1.5 rounded-md border text-[11px] font-medium transition-colors ${
                                            form.embed_mobile_variant === v
                                                ? "bg-blue-50 border-blue-500 text-blue-700"
                                                : "bg-slate-50 border-slate-200 text-slate-600 hover:border-slate-400"
                                        }`}
                                    >{v === "inherit" ? "Same as desktop" : v === "labeled" ? "Labeled pill" : "Bubble"}</button>
                                ))}
                            </div>
                        </Field>
                        <Field label="Mobile widget position">
                            <PositionPicker
                                value={form.embed_mobile_position}
                                onChange={(v) => setForm({ ...form, embed_mobile_position: v })}
                                includeInherit={true}
                                testidPrefix="embed-mobile-position"
                            />
                        </Field>
                    </Section>

                    <Section title="Widget Colors" subtitle="Pill, iframe header, and accent — pick three colours to brand the entire checkout.">
                        <Field label="Label text"><input data-testid="embed-label" maxLength={40} value={form.embed_label} onChange={(e) => setForm({ ...form, embed_label: e.target.value })} className="input" /></Field>
                        <div className="grid grid-cols-2 gap-2">
                            <Field label="Pill background"><ColorInput value={form.embed_bg_color} onChange={(v) => setForm({ ...form, embed_bg_color: v })} testid="embed-bg" /></Field>
                            <Field label="Pill text"><ColorInput value={form.embed_text_color} onChange={(v) => setForm({ ...form, embed_text_color: v })} testid="embed-fg" /></Field>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                            <Field label="Modal header bg" hint="Empty = inherit pill background.">
                                <ColorInput
                                    value={form.embed_header_color || form.embed_bg_color}
                                    onChange={(v) => setForm({ ...form, embed_header_color: v })}
                                    testid="embed-header-bg" />
                            </Field>
                            <Field label="Modal header text" hint="Empty = inherit pill text.">
                                <ColorInput
                                    value={form.embed_header_text_color || form.embed_text_color}
                                    onChange={(v) => setForm({ ...form, embed_header_text_color: v })}
                                    testid="embed-header-fg" />
                            </Field>
                        </div>
                        <Field label="CTA / accent color" hint="Used on Submit buttons inside the iframe. Empty = pill background.">
                            <ColorInput
                                value={form.embed_accent_color || form.embed_bg_color}
                                onChange={(v) => setForm({ ...form, embed_accent_color: v })}
                                testid="embed-accent" />
                        </Field>
                        <Field label="Shape (border radius px)" hint="999 = pill, 8 = rounded square, 0 = sharp">
                            <input type="number" min={0} max={999} value={form.embed_border_radius_px}
                                   data-testid="embed-radius"
                                   onChange={(e) => setForm({ ...form, embed_border_radius_px: Math.max(0, Math.min(999, parseInt(e.target.value || "0", 10))) })}
                                   className="input" />
                        </Field>
                        <Field label="Size" hint="Compact = denser layouts, Large = hero CTA.">
                            <div className="flex gap-1.5" data-testid="embed-size-picker">
                                {SIZE_PRESETS.map((s) => (
                                    <button key={s.id} type="button"
                                        data-testid={`embed-size-${s.id}`}
                                        onClick={() => setForm({ ...form, embed_size: s.id })}
                                        className={`flex-1 px-2 py-1.5 rounded-md border text-xs font-medium transition-colors ${
                                            form.embed_size === s.id
                                                ? "bg-blue-50 border-blue-500 text-blue-700"
                                                : "bg-slate-50 border-slate-200 text-slate-600 hover:border-slate-400"
                                        }`}
                                    >{s.label}</button>
                                ))}
                            </div>
                        </Field>
                        <Field label="Style preset" hint="Layers an animation or visual treatment on top of your colours. Pulse + Shimmer use CSS keyframes — smooth and battery-friendly.">
                            <div className="grid grid-cols-5 gap-1.5" data-testid="embed-style-picker">
                                {STYLE_PRESETS.map((s) => (
                                    <button key={s.id} type="button"
                                        data-testid={`embed-style-${s.id}`}
                                        onClick={() => setForm({ ...form, embed_style: s.id })}
                                        title={s.hint}
                                        className={`px-2 py-1.5 rounded-md border text-[11px] font-medium transition-colors ${
                                            form.embed_style === s.id
                                                ? "bg-blue-50 border-blue-500 text-blue-700"
                                                : "bg-slate-50 border-slate-200 text-slate-600 hover:border-slate-400"
                                        }`}
                                    >{s.label}</button>
                                ))}
                            </div>
                        </Field>
                        <Field label="Sticker (icon)" hint="15 polished icons. Hidden if a Logo URL is set.">
                            <StickerPicker value={form.embed_sticker} onChange={(v) => setForm({ ...form, embed_sticker: v })} />
                        </Field>
                        <Field label="Logo URL (optional)" hint="https:// or data:image/... — rendered as 18×18 circle on the left of the label. Overrides the sticker.">
                            <input data-testid="embed-logo" value={form.embed_logo_url || ""} onChange={(e) => setForm({ ...form, embed_logo_url: e.target.value })} className="input" placeholder="https://…" />
                        </Field>
                    </Section>

                    <Section title="Click behaviour">
                        <Field label="Open mode" hint="What happens when the customer clicks the pill (or a manual button).">
                            <select data-testid="embed-open-mode" value={form.embed_open_mode}
                                    onChange={(e) => setForm({ ...form, embed_open_mode: e.target.value })}
                                    className="select">
                                <option value="iframe">Iframe modal (default — stays on merchant site)</option>
                                <option value="popup">Popup window (480×780, centered)</option>
                                <option value="redirect">Redirect current tab</option>
                            </select>
                        </Field>
                        <Field label="Suggested amount formula">
                            <select data-testid="embed-amount-mode" value={form.embed_amount_mode}
                                    onChange={(e) => setForm({ ...form, embed_amount_mode: e.target.value })}
                                    className="select">
                                <option value="blank">Empty — customer types it</option>
                                <option value="topup">Top-up: threshold − balance</option>
                                <option value="fixed">Fixed amount</option>
                            </select>
                        </Field>
                        {form.embed_amount_mode === "fixed" && (
                            <Field label="Fixed amount (₹)">
                                <input type="number" min={0} value={form.embed_suggested_fixed ?? 0}
                                       data-testid="embed-fixed-amount"
                                       onChange={(e) => setForm({ ...form, embed_suggested_fixed: parseFloat(e.target.value || "0") })}
                                       className="input" />
                            </Field>
                        )}
                        <Toggle label="Auto-close on success" hint="Dismiss the iframe modal automatically when the customer's payment is approved (best UX on iframe mode)."
                                checked={form.embed_auto_close_on_success}
                                onChange={(v) => setForm({ ...form, embed_auto_close_on_success: v })}
                                testid="embed-auto-close" />
                    </Section>

                    <Section title="Allowed host domains (CORS)" subtitle="Empty list = no restriction (dev). Add the exact origins where this snippet should work.">
                        <div className="flex gap-2 items-stretch">
                            <input value={originDraft}
                                   data-testid="embed-origin-input"
                                   onChange={(e) => setOriginDraft(e.target.value)}
                                   onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addOrigin(); }}}
                                   placeholder="https://mahitoys.com"
                                   className="input flex-1" />
                            <button type="button" onClick={addOrigin} data-testid="embed-add-origin"
                                    className="px-3 py-1.5 bg-slate-900 text-white text-xs rounded-md hover:bg-slate-700">Add</button>
                        </div>
                        {(form.embed_allowed_origins || []).length > 0 && (
                            <div className="flex flex-wrap gap-1.5 mt-2">
                                {form.embed_allowed_origins.map((o) => (
                                    <span key={o} className="inline-flex items-center gap-1 text-[11px] bg-slate-100 text-slate-700 rounded px-2 py-0.5 font-mono">
                                        {o}
                                        <button type="button" onClick={() => setForm({ ...form, embed_allowed_origins: form.embed_allowed_origins.filter((x) => x !== o) })}
                                                className="hover:text-rose-600"><X className="w-3 h-3" /></button>
                                    </span>
                                ))}
                            </div>
                        )}
                    </Section>

                    <div className="sticky bottom-0 bg-white/90 backdrop-blur border-t border-slate-200 pt-3 -mx-1 px-1">
                        <button data-testid="embed-save" disabled={busy} onClick={save}
                                className="btn-primary px-4 py-2 rounded-md text-sm inline-flex items-center gap-1.5">
                            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                            Save widget config
                        </button>
                        {err && <span className="ml-3 text-xs text-rose-600">{err}</span>}
                    </div>
                </div>

                {/* ── RIGHT: Live preview + snippet ──────────────── */}
                <div className="space-y-4 lg:sticky lg:top-4 lg:self-start">
                    <WidgetAnalyticsCard linkCode={link.link_code} />
                    <div className="border border-slate-200 rounded-lg overflow-hidden bg-gradient-to-br from-slate-50 to-slate-100">
                        <div className="px-3 py-2 text-[11px] uppercase tracking-wider text-slate-500 font-semibold border-b border-slate-200 bg-white/60 flex items-center justify-between">
                            <span>Live preview · mahitoys.com</span>
                            <label className="text-[10px] inline-flex items-center gap-1 font-normal text-slate-500">
                                Simulated balance ₹
                                <input type="number" value={simBalance} min={0}
                                       data-testid="preview-sim-balance"
                                       onChange={(e) => setSimBalance(parseFloat(e.target.value || "0"))}
                                       className="w-16 px-1.5 py-0.5 border border-slate-300 rounded text-[11px]" />
                            </label>
                        </div>
                        <PreviewCanvas form={form} simBalance={simBalance} />
                    </div>

                    {/* Snippet to paste */}
                    <div className="card p-4">
                        <div className="flex items-center justify-between mb-2">
                            <h4 className="text-sm font-semibold">Drop this on your site</h4>
                            <SnippetCopy snippet={snippet} copied={copied} setCopied={setCopied} />
                        </div>
                        <pre data-testid="embed-snippet"
                             className="text-[11px] font-mono bg-slate-50 border border-slate-200 rounded p-3 overflow-x-auto whitespace-pre-wrap break-all leading-relaxed">
{snippet?.snippet || "<loading…>"}
{`
<!-- Optional host context for conditional display: -->
<script>
  window.PayGate = window.PayGate || {};
  window.PayGate.context = { user_id: "ORDER_123", balance: 50 };
</script>`}
                        </pre>
                    </div>
                </div>
            </div>
            {grabberModalOpen && (
                <GrabberGalleryModal
                    value={form.embed_grabber_preset}
                    onSelect={(presetId, customUrl) => {
                        if (customUrl) {
                            setForm({ ...form, embed_grabber_image_url: customUrl, embed_grabber_preset: "none" });
                        } else {
                            setForm({ ...form, embed_grabber_preset: presetId, embed_grabber_image_url: "" });
                        }
                        setGrabberModalOpen(false);
                    }}
                    onClose={() => setGrabberModalOpen(false)}
                />
            )}
        </div>
    );
}

// Sticker presets — must stay in lockstep with the STICKERS map in
// /app/backend/server.py so the live preview matches what embed.js
// ultimately renders. Each entry is an inline SVG that inherits
// currentColor so the icon picks up `embed_text_color` automatically.
const STICKER_PRESETS = [
    { id: "none",    label: "None",    svg: null },
    { id: "bolt",    label: "Bolt",    svg: <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M13 2L4.09 12.97a.5.5 0 00.39.83H11l-1 8.2 8.91-10.97a.5.5 0 00-.39-.83H13l1-8.2z"/></svg> },
    { id: "shield",  label: "Shield",  svg: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg> },
    { id: "card",    label: "Card",    svg: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><rect x="2.5" y="5" width="19" height="14" rx="2"/><path d="M2.5 10h19"/></svg> },
    { id: "lock",    label: "Lock",    svg: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><rect x="4" y="11" width="16" height="10" rx="2"/><path d="M8 11V7a4 4 0 018 0v4"/></svg> },
    { id: "sparkle", label: "Sparkle", svg: <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l1.6 5.4L19 9l-5.4 1.6L12 16l-1.6-5.4L5 9l5.4-1.6L12 2zm7 11l.8 2.7L22 16l-2.2.7L19 19l-.8-2.3L16 16l2.2-.7L19 13z"/></svg> },
    { id: "arrow",   label: "Arrow",   svg: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 6l6 6-6 6"/></svg> },
    { id: "wallet",  label: "Wallet",  svg: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M3 7v10a2 2 0 002 2h15a1 1 0 001-1V9a1 1 0 00-1-1H5a2 2 0 010-4h14"/><circle cx="17" cy="13" r="1.3" fill="currentColor"/></svg> },
    { id: "rupee",   label: "Rupee",   svg: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M6 4h12M6 9h12M7 14h5a4 4 0 000-8H7M13 22l-7-8"/></svg> },
    { id: "gift",    label: "Gift",    svg: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 12v10H4V12M2 7h20v5H2zM12 22V7M12 7H7.5a2.5 2.5 0 010-5C11 2 12 7 12 7zm0 0h4.5a2.5 2.5 0 000-5C13 2 12 7 12 7z"/></svg> },
    { id: "fire",    label: "Fire",    svg: <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M13.5.67s.74 2.65.74 4.8c0 2.06-1.35 3.73-3.41 3.73-2.07 0-3.63-1.67-3.63-3.73l.03-.36C5.21 7.51 4 10.62 4 14a8 8 0 0016 0c0-4.16-2-7.88-6.5-13.33zM11.71 19c-1.78 0-3.22-1.4-3.22-3.14 0-1.62 1.05-2.76 2.81-3.12 1.77-.36 3.6-1.21 4.62-2.58.39 1.29.59 2.65.59 4.04 0 2.65-2.15 4.8-4.8 4.8z"/></svg> },
    { id: "star",    label: "Star",    svg: <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M12 .587l3.668 7.568 8.332 1.151-6.064 5.828 1.48 8.279L12 19.446l-7.416 3.967 1.48-8.279L0 9.306l8.332-1.151z"/></svg> },
    { id: "rocket",  label: "Rocket",  svg: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 00-2.91-.09zM12 15l-3-3a22 22 0 012-3.95A12.88 12.88 0 0122 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 01-4 2z"/><path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5"/></svg> },
    { id: "crown",   label: "Crown",   svg: <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M5 16L3 5l5.5 5L12 4l3.5 6L21 5l-2 11H5zm0 2h14v2H5z"/></svg> },
    { id: "clock",   label: "Clock",   svg: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg> },
    { id: "gem",     label: "Gem",     svg: <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M6 3h12l4 6-10 13L2 9l4-6z"/></svg> },
];

// Style presets — mirror the STYLES map in server.py. The preview
// renders them with inline CSS so the merchant sees exactly what
// embed.js will produce on the host site.
const STYLE_PRESETS = [
    { id: "solid",    label: "Solid",    hint: "Flat brand colour" },
    { id: "outlined", label: "Outlined", hint: "Border + transparent" },
    { id: "glow",     label: "Glow",     hint: "Soft halo (premium feel)" },
    { id: "pulse",    label: "Pulse",    hint: "Gentle pulsing (urgency)" },
    { id: "shimmer",  label: "Shimmer",  hint: "Gradient sweep (Apple/Stripe feel)" },
];

const SIZE_PRESETS = [
    { id: "compact", label: "Compact" },
    { id: "default", label: "Default" },
    { id: "large",   label: "Large" },
];

function StickerPicker({ value, onChange }) {
    // Renders the 8 preset stickers as a single horizontally-scrollable
    // strip — small chips with the icon centered on a neutral grey bg.
    // Active chip gets a blue ring so the choice is unmistakable. We
    // avoid a dropdown here because seeing the icon is half the value;
    // a text list would defeat the purpose.
    return (
        <div data-testid="embed-sticker-picker" className="flex gap-1.5 overflow-x-auto pb-1">
            {STICKER_PRESETS.map((s) => {
                const active = value === s.id;
                return (
                    <button
                        type="button"
                        key={s.id}
                        data-testid={`embed-sticker-${s.id}`}
                        onClick={() => onChange(s.id)}
                        title={s.label}
                        className={`flex-shrink-0 w-12 h-12 rounded-md border flex flex-col items-center justify-center gap-0.5 transition-all ${
                            active
                                ? "bg-blue-50 border-blue-500 ring-2 ring-blue-200 text-blue-700"
                                : "bg-slate-50 border-slate-200 text-slate-500 hover:border-slate-400 hover:text-slate-700"
                        }`}
                    >
                        <div className="flex items-center justify-center">
                            {s.svg || <span className="text-[10px] font-bold">—</span>}
                        </div>
                        <div className="text-[9px] leading-none">{s.label}</div>
                    </button>
                );
            })}
        </div>
    );
}

function PreviewCanvas({ form, simBalance }) {
    // Honour the actual CFG.shouldShowFloating() logic from embed.js so
    // the merchant sees exactly when their pill will show / hide.
    const visible = useMemo(() => {
        if (form.embed_enabled === false) return false;
        if (!form.embed_floating_mode || form.embed_floating_mode === "off") return false;
        // Mirror embed.js: gating must be ON for the threshold check to apply.
        if (!form.embed_balance_gating) return true;
        if (form.embed_balance_threshold == null || form.embed_balance_threshold === "") return true;
        return Number(simBalance) < Number(form.embed_balance_threshold);
    }, [form, simBalance]);

    const positionStyle = useMemo(() => positionToStyle(form.embed_floating_mode), [form.embed_floating_mode]);
    const isBubble = form.embed_variant === "bubble";
    // When the pill is anchored to the top of the viewport, the grabber
    // should appear BELOW the pill (toward the page centre — where the
    // customer is reading). When at middle/bottom, grabber sits ABOVE.
    // This mirrors how Tawk.to handles top-anchored chats and keeps the
    // grabber visually adjacent to the pill instead of floating off-screen.
    const isTopAnchored = String(form.embed_floating_mode || "").startsWith("top-");

    // Resolved grabber preview URL — mirrors what embed.js will load.
    // image_url wins over preset. Both null = grabber hidden in preview.
    const grabberUrl = useMemo(() => {
        if (!form.embed_grabber_enabled) return null;
        if (form.embed_grabber_image_url) return form.embed_grabber_image_url;
        if (form.embed_grabber_preset && form.embed_grabber_preset !== "none") {
            return GRABBER_PRESETS_MAP[form.embed_grabber_preset]?.svgDataUrl || null;
        }
        return null;
    }, [form.embed_grabber_enabled, form.embed_grabber_image_url, form.embed_grabber_preset]);

    // Resolved sticker SVG for the live preview — mirrors what embed.js
    // will render on the host page.
    const stickerSvg = useMemo(() => {
        if (form.embed_logo_url) return null; // logo overrides sticker
        const preset = STICKER_PRESETS.find((s) => s.id === form.embed_sticker);
        return preset?.svg || null;
    }, [form.embed_sticker, form.embed_logo_url]);

    // Resolve size + style preset overrides. Same logic as embed.js so
    // the preview matches the production widget pixel-for-pixel.
    const sizePreset = useMemo(() => {
        switch (form.embed_size) {
            case "compact": return { padding: "8px 14px", fontSize: 13 };
            case "large":   return { padding: "16px 24px", fontSize: 16 };
            default:        return { padding: "12px 18px", fontSize: 14 };
        }
    }, [form.embed_size]);

    const styleOverrides = useMemo(() => {
        const bg = form.embed_bg_color || "#0f172a";
        const txt = form.embed_text_color || "#ffffff";
        const hexToRgba = (hex, a) => {
            let h = (hex || "").replace("#", "");
            if (h.length === 3) h = h.split("").map((c) => c + c).join("");
            const r = parseInt(h.slice(0, 2), 16) || 0;
            const g = parseInt(h.slice(2, 4), 16) || 0;
            const b = parseInt(h.slice(4, 6), 16) || 0;
            return `rgba(${r},${g},${b},${a})`;
        };
        switch (form.embed_style) {
            case "outlined":
                return { background: "transparent", color: bg, border: `2px solid ${bg}`, boxShadow: "none" };
            case "glow":
                return { boxShadow: `0 0 0 4px ${hexToRgba(bg, 0.25)}, 0 6px 18px ${hexToRgba(bg, 0.45)}` };
            case "pulse":
                return { animation: "paygatePreviewPulse 1.8s ease-in-out infinite" };
            case "shimmer":
                return {
                    backgroundImage: `linear-gradient(105deg, transparent 30%, ${hexToRgba("#ffffff", 0.35)} 50%, transparent 70%), linear-gradient(${bg}, ${bg})`,
                    backgroundSize: "200% 100%, 100% 100%",
                    backgroundPosition: "100% 0, 0 0",
                    animation: "paygatePreviewShimmer 2.5s ease-in-out infinite",
                };
            default:
                return {};
        }
    }, [form.embed_style, form.embed_bg_color, form.embed_text_color]);

    return (
        <div data-testid="embed-preview" className="relative h-[460px] bg-white">
            {/* Inline keyframes for the preview's pulse/shimmer presets.
                Scoped via unique animation-names (paygatePreview*) so they
                never conflict with the host site's own CSS. */}
            <style>{`
                @keyframes paygatePreviewPulse {
                    0%, 100% { transform: scale(1); }
                    50% { transform: scale(1.05); }
                }
                @keyframes paygatePreviewShimmer {
                    0% { background-position: 100% 0, 0 0; }
                    100% { background-position: -100% 0, 0 0; }
                }
            `}</style>
            {/* fake host page chrome — gives the merchant something concrete
                to compare against */}
            <div className="absolute inset-0 p-4 text-[10px] text-slate-400 leading-relaxed">
                <div className="h-6 bg-slate-100 rounded w-1/3 mb-2" />
                <div className="h-2 bg-slate-100 rounded w-2/3 mb-1" />
                <div className="h-2 bg-slate-100 rounded w-3/4 mb-1" />
                <div className="h-2 bg-slate-100 rounded w-1/2" />
                <div className="mt-4 inline-block bg-amber-100 text-amber-700 px-2 py-1 rounded text-[10px]">
                    Current balance ₹{simBalance}{" "}
                    {form.embed_balance_gating
                        ? (form.embed_balance_threshold != null && form.embed_balance_threshold !== "" && <span className="ml-1 opacity-60">· threshold ₹{form.embed_balance_threshold}</span>)
                        : <span className="ml-1 opacity-60">· gating OFF (always show)</span>}
                </div>
            </div>
            {visible && (
                <div
                    style={{
                        position: "absolute",
                        ...positionStyle,
                        display: "flex",
                        flexDirection: isTopAnchored ? "column-reverse" : "column",
                        alignItems: positionStyle.right != null ? "flex-end" : positionStyle.left === "50%" ? "center" : "flex-start",
                        gap: 8,
                        maxWidth: 200,
                    }}
                >
                    {grabberUrl && (
                        <div
                            data-testid="embed-preview-grabber"
                            style={{
                                width: 140, maxWidth: "80vw",
                                borderRadius: 12, overflow: "hidden",
                                boxShadow: "0 8px 22px rgba(15,23,42,.18)",
                                background: form.embed_match_bubble_theme ? form.embed_bg_color : "#ffffff",
                            }}
                        >
                            <img src={grabberUrl} alt="" style={{ display: "block", width: "100%", height: "auto" }} />
                        </div>
                    )}
                    <button
                        type="button"
                        data-testid="embed-preview-pill"
                        style={isBubble ? {
                            background: form.embed_bg_color,
                            color: form.embed_text_color,
                            width: 52, height: 52,
                            borderRadius: "50%", border: 0,
                            cursor: "pointer",
                            boxShadow: "0 6px 18px rgba(15,23,42,.18), 0 2px 4px rgba(15,23,42,.10)",
                            display: "inline-flex", alignItems: "center", justifyContent: "center",
                            ...styleOverrides,
                        } : {
                            background: form.embed_bg_color,
                            color: form.embed_text_color,
                            borderRadius: `${form.embed_border_radius_px}px`,
                            padding: sizePreset.padding,
                            border: 0,
                            fontWeight: 600,
                            fontSize: sizePreset.fontSize,
                            cursor: "pointer",
                            boxShadow: "0 6px 18px rgba(15,23,42,.18), 0 2px 4px rgba(15,23,42,.10)",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 8,
                            ...styleOverrides,
                        }}
                    >
                        {form.embed_logo_url ? (
                            <img src={form.embed_logo_url} alt="" style={{ width: isBubble ? 26 : 18, height: isBubble ? 26 : 18, borderRadius: "50%", objectFit: "cover" }} />
                        ) : stickerSvg ? (
                            <span style={{ display: "inline-flex", alignItems: "center", lineHeight: 0, transform: isBubble ? "scale(1.6)" : "none" }}>{stickerSvg}</span>
                        ) : isBubble ? (
                            <span style={{ fontSize: 22 }}>💬</span>
                        ) : null}
                        {!isBubble && <span>{form.embed_label || "Pay Now"}</span>}
                    </button>
                </div>
            )}
            {!visible && (
                <div className="absolute bottom-2 inset-x-2 text-center text-[10px] text-slate-400 italic">
                    Pill hidden — {form.embed_floating_mode === "off"
                        ? "floating mode is OFF"
                        : "simulated balance is at/above threshold"}
                </div>
            )}
        </div>
    );
}

function SnippetCopy({ snippet, copied, setCopied }) {
    const ref = useRef(null);
    const copy = async () => {
        try {
            await navigator.clipboard.writeText(snippet?.snippet || "");
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        } catch (e) { console.warn("clipboard write denied:", e); }
    };
    return (
        <button ref={ref} onClick={copy} disabled={!snippet}
                data-testid="embed-copy-snippet"
                className="text-[11px] inline-flex items-center gap-1 px-2 py-1 rounded border border-slate-200 hover:bg-slate-50 disabled:opacity-50">
            {copied ? <Check className="w-3 h-3 text-emerald-600" /> : <Copy className="w-3 h-3" />}
            {copied ? "Copied" : "Copy"}
        </button>
    );
}

// -- Tiny stylistic primitives kept inline (no design-system tokens hijack)
function Section({ title, subtitle, children }) {
    return (
        <div className="card p-4 space-y-3">
            <div>
                <h3 className="font-semibold text-sm">{title}</h3>
                {subtitle && <p className="text-[11px] text-slate-500 mt-0.5 leading-snug">{subtitle}</p>}
            </div>
            {children}
        </div>
    );
}
function Field({ label, hint, children }) {
    return (
        <label className="block">
            <div className="text-[11px] font-medium text-slate-700 mb-1">{label}</div>
            {children}
            {hint && <div className="text-[10px] text-slate-500 mt-0.5 leading-snug">{hint}</div>}
        </label>
    );
}
function Toggle({ label, hint, checked, onChange, testid }) {
    return (
        <label className="flex items-start gap-2 cursor-pointer">
            <input type="checkbox" data-testid={testid} checked={!!checked} onChange={(e) => onChange(e.target.checked)} className="mt-0.5" />
            <div>
                <div className="text-xs font-medium text-slate-800">{label}</div>
                {hint && <div className="text-[10px] text-slate-500 leading-snug mt-0.5">{hint}</div>}
            </div>
        </label>
    );
}
function ColorInput({ value, onChange, testid }) {
    return (
        <div className="flex items-center gap-1">
            <input type="color" value={value} data-testid={testid + "-picker"}
                   onChange={(e) => onChange(e.target.value)}
                   className="w-9 h-9 rounded cursor-pointer border border-slate-300" />
            <input type="text" value={value} data-testid={testid + "-hex"} maxLength={9}
                   onChange={(e) => onChange(e.target.value)}
                   className="input flex-1 font-mono text-xs" />
        </div>
    );
}

/**
 * Widget funnel — last-7-days impressions / clicks / conversions for
 * this link. Computed server-side via `/partner/embed/analytics`.
 *
 * Hidden when there's literally zero data so brand-new links don't show
 * a meaningless "0 / 0 / 0" card — replaced with a friendly hint that
 * data will appear once the snippet is live.
 */
function WidgetAnalyticsCard({ linkCode }) {
    const [data, setData] = useState(null);
    const [days, setDays] = useState(7);
    useEffect(() => {
        api.get("/partner/embed/analytics", { params: { link_code: linkCode, days } })
            .then((r) => setData(r.data)).catch(() => setData({ items: [] }));
    }, [linkCode, days]);
    if (!data) return <div className="card p-4 text-sm text-slate-400"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading widget analytics…</div>;
    const row = (data.items || [])[0] || { impressions: 0, clicks: 0, conversions: 0, ctr: null, cr: null };
    const hasData = (row.impressions || 0) + (row.clicks || 0) > 0;
    return (
        <div className="card p-4" data-testid="widget-analytics-card">
            <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-semibold">Widget performance</h4>
                <select value={days} onChange={(e) => setDays(parseInt(e.target.value, 10))}
                        data-testid="widget-analytics-days"
                        className="text-[11px] px-2 py-1 border border-slate-200 rounded">
                    <option value={1}>Last 24h</option>
                    <option value={7}>Last 7 days</option>
                    <option value={30}>Last 30 days</option>
                </select>
            </div>
            {!hasData ? (
                <div className="text-xs text-slate-500 leading-relaxed">
                    No widget activity yet. Once a customer loads a page with your snippet,
                    impressions and clicks will appear here within seconds.
                </div>
            ) : (
                <div className="grid grid-cols-3 gap-2 text-center">
                    <Stat label="Impressions" value={row.impressions} sub="unique sessions" />
                    <Stat label="Clicks" value={row.clicks} sub={row.ctr != null ? `CTR ${row.ctr}%` : "—"} accent="violet" />
                    <Stat label="Conversions" value={row.conversions}
                          sub={row.cr != null ? `CR ${row.cr}%` : "—"} accent="emerald" />
                </div>
            )}
        </div>
    );
}

function Stat({ label, value, sub, accent }) {
    const c = accent === "emerald" ? "text-emerald-700"
            : accent === "violet" ? "text-violet-700"
            : "text-slate-900";
    return (
        <div className="bg-slate-50/60 rounded p-2">
            <div className="text-[10px] uppercase tracking-wider text-slate-400">{label}</div>
            <div className={`font-mono font-bold mt-0.5 text-lg ${c}`}>{value ?? 0}</div>
            <div className="text-[10px] text-slate-500 mt-0.5 truncate">{sub}</div>
        </div>
    );
}


// -- New helpers (Phase 3) ────────────────────────────────────────────

// 8-position layout map. `inherit` and `off` are also accepted by the
// PositionPicker but rendered with distinct visual states.
const POSITIONS_8 = [
    "top-left", "top-center", "top-right",
    "middle-left", null, "middle-right",
    "bottom-left", "bottom-center", "bottom-right",
];

// Convert a floating_mode string into a {position, offsets} CSS style
// for the live preview. Mirrors positionCss() in embed.js so the
// preview reflects production pixel-for-pixel.
function positionToStyle(m) {
    switch (m) {
        case "top-left":      return { top: 16, left: 16 };
        case "top-center":    return { top: 16, left: "50%", transform: "translateX(-50%)" };
        case "top-right":     return { top: 16, right: 16 };
        case "middle-left":   return { top: "50%", left: 16, transform: "translateY(-50%)" };
        case "middle-right":  return { top: "50%", right: 16, transform: "translateY(-50%)" };
        case "bottom-left":   return { bottom: 16, left: 16 };
        case "bottom-center": return { bottom: 16, left: "50%", transform: "translateX(-50%)" };
        case "bottom-right":
        default:              return { bottom: 16, right: 16 };
    }
}

// Visual 3×3 grid position picker. Each cell is a tiny 3×3 mini-grid
// with the chosen cell highlighted — same UX Tawk.to ships. The merchant
// clicks a chip; selection is unmistakable from the green-highlighted
// cell.
function PositionPicker({ value, onChange, includeOff = false, includeInherit = false, testidPrefix }) {
    const options = useMemo(() => {
        const arr = POSITIONS_8.filter(Boolean);
        if (includeOff) arr.unshift("off");
        if (includeInherit) arr.unshift("inherit");
        return arr;
    }, [includeOff, includeInherit]);
    return (
        <div className="flex flex-wrap gap-1.5" data-testid={`${testidPrefix}-picker`}>
            {options.map((opt) => (
                <PositionChip
                    key={opt}
                    pos={opt}
                    active={value === opt}
                    onClick={() => onChange(opt)}
                    testid={`${testidPrefix}-${opt}`}
                />
            ))}
        </div>
    );
}
function PositionChip({ pos, active, onClick, testid }) {
    // Render a tiny 3×3 grid where the cell matching `pos` is filled
    // with the brand colour (emerald, à la the reference image).
    // Special-cases:
    //   off     → empty grid + "Off" pill
    //   inherit → empty grid + "Same as desktop" pill
    const isSpecial = pos === "off" || pos === "inherit";
    const cellFor = (idx) => {
        if (isSpecial) return false;
        return POSITIONS_8[idx] === pos;
    };
    return (
        <button
            type="button"
            data-testid={testid}
            onClick={onClick}
            title={pos}
            className={`relative inline-flex flex-col items-center gap-1 p-2 rounded-md border transition-colors ${
                active
                    ? "bg-emerald-50 border-emerald-500 ring-2 ring-emerald-200"
                    : "bg-white border-slate-200 hover:border-slate-400"
            }`}
        >
            <div className="grid grid-cols-3 gap-0.5" style={{ width: 28, height: 28 }}>
                {Array.from({ length: 9 }).map((_, idx) => (
                    <div key={POSITIONS_8[idx] || `cell-${idx}`}
                         className={`rounded-[1px] border ${
                             cellFor(idx) ? "bg-emerald-600 border-emerald-700" : "bg-white border-slate-300"
                         }`} />
                ))}
            </div>
            <span className="text-[9px] leading-none text-slate-500">
                {isSpecial ? (pos === "off" ? "Off" : "Same") : "·"}
            </span>
        </button>
    );
}

// Pill-variant picker: two visual cards (bubble vs labeled).
function VariantPicker({ value, onChange, bg, fg, label, testidPrefix }) {
    const variants = [
        { id: "bubble",  hint: "Icon-only circle (chat-style)" },
        { id: "labeled", hint: "Sticker + text (CTA pill)" },
    ];
    return (
        <div className="grid grid-cols-2 gap-2" data-testid={`${testidPrefix}-picker`}>
            {variants.map((v) => {
                const active = value === v.id;
                return (
                    <button
                        type="button"
                        key={v.id}
                        data-testid={`${testidPrefix}-${v.id}`}
                        onClick={() => onChange(v.id)}
                        className={`p-3 rounded-lg border transition-all flex items-center justify-between ${
                            active
                                ? "bg-emerald-50 border-emerald-500 ring-2 ring-emerald-200"
                                : "bg-white border-slate-200 hover:border-slate-400"
                        }`}
                    >
                        <div className="text-left">
                            <div className="text-xs font-semibold capitalize">{v.id}</div>
                            <div className="text-[10px] text-slate-500 mt-0.5">{v.hint}</div>
                        </div>
                        {v.id === "bubble" ? (
                            <div style={{
                                width: 36, height: 36, borderRadius: "50%",
                                background: bg, color: fg,
                                display: "inline-flex", alignItems: "center", justifyContent: "center",
                                boxShadow: "0 2px 6px rgba(15,23,42,.20)",
                            }}>
                                <span style={{ fontSize: 16 }}>💬</span>
                            </div>
                        ) : (
                            <div style={{
                                background: bg, color: fg,
                                borderRadius: 999, padding: "6px 12px",
                                fontSize: 11, fontWeight: 700,
                                boxShadow: "0 2px 6px rgba(15,23,42,.20)",
                            }}>{label || "Pay Now"}</div>
                        )}
                    </button>
                );
            })}
        </div>
    );
}

// Grabber preset registry — mirrors backend/core/embed_grabbers.py.
// `svgDataUrl` is a data: URL of the same SVG the backend serves, so
// the customizer's live preview matches the production widget. Kept in
// the same file (instead of a JSON import) so the bundle stays slim.
const G_PRESETS = [
    { id: "pay_now",        label: "Pay Now!",        category: "general" },
    { id: "quick_deposit",  label: "Quick Deposit",   category: "general" },
    { id: "cashback_back",  label: "5% Cashback",     category: "general" },
    { id: "vip_only",       label: "VIP Only",        category: "general" },
    { id: "top_up_balance", label: "Top Up Balance",  category: "general" },
    { id: "need_help",      label: "Need Help?",      category: "general" },
    { id: "diwali",         label: "Diwali",          category: "seasonal" },
    { id: "holi",           label: "Holi",            category: "seasonal" },
    { id: "new_year",       label: "New Year",        category: "seasonal" },
    { id: "christmas",      label: "Christmas",       category: "seasonal" },
    { id: "ipl_season",     label: "IPL 2026",        category: "seasonal" },
    { id: "cricket_world_cup", label: "Cricket WC",   category: "seasonal" },
];
// Lazy-load the SVG previews from the backend's public/embed-config
// endpoint OR fall back to hitting a thumbnail proxy. For simplicity we
// just point at /api/public/embed-grabber/{preset} (added next).
const GRABBER_PRESETS_MAP = Object.fromEntries(G_PRESETS.map((p) => [
    p.id, { ...p, svgDataUrl: `${process.env.REACT_APP_BACKEND_URL}/api/public/embed-grabber/${p.id}.svg` },
]));

function GrabberGalleryModal({ value, onSelect, onClose }) {
    const [tab, setTab] = useState("general");
    const [customUrl, setCustomUrl] = useState("");
    const items = G_PRESETS.filter((p) => p.category === tab);
    return (
        <div
            data-testid="grabber-gallery-modal"
            className="fixed inset-0 z-[60] bg-black/40 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
        >
            <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[85vh] overflow-hidden flex flex-col">
                <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
                    <div>
                        <h3 className="font-bold text-slate-900">Select Attention Grabber</h3>
                        <p className="text-[11px] text-slate-500 mt-0.5">Pick a curated illustration or upload your own image.</p>
                    </div>
                    <button data-testid="grabber-gallery-close" onClick={onClose}
                            className="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 inline-flex items-center justify-center">
                        <X className="w-4 h-4 text-slate-600" />
                    </button>
                </div>
                <div className="px-5 pt-3 flex gap-2">
                    {["general", "seasonal"].map((t) => (
                        <button key={t}
                                data-testid={`grabber-tab-${t}`}
                                onClick={() => setTab(t)}
                                className={`px-3 py-1.5 rounded-md text-xs font-semibold capitalize ${
                                    tab === t
                                        ? "bg-emerald-600 text-white"
                                        : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                                }`}
                        >{t}</button>
                    ))}
                </div>
                <div className="px-5 py-4 overflow-y-auto flex-1">
                    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
                        {items.map((p) => {
                            const active = value === p.id;
                            return (
                                <button
                                    key={p.id}
                                    data-testid={`grabber-preset-${p.id}`}
                                    onClick={() => onSelect(p.id, null)}
                                    className={`p-2 rounded-lg border transition-all ${
                                        active
                                            ? "border-emerald-500 ring-2 ring-emerald-200 bg-emerald-50"
                                            : "border-slate-200 hover:border-slate-400 bg-slate-50"
                                    }`}
                                >
                                    <div className="bg-white rounded-md overflow-hidden aspect-[180/130] flex items-center justify-center">
                                        <img src={GRABBER_PRESETS_MAP[p.id].svgDataUrl} alt={p.label}
                                             style={{ display: "block", width: "100%", height: "100%", objectFit: "cover" }} />
                                    </div>
                                    <div className="text-[10px] text-slate-600 mt-1 text-center font-medium">{p.label}</div>
                                </button>
                            );
                        })}
                    </div>
                </div>
                <div className="px-5 py-3 border-t border-slate-200 bg-slate-50 flex items-center gap-2">
                    <input
                        type="text"
                        data-testid="grabber-custom-url"
                        value={customUrl}
                        onChange={(e) => setCustomUrl(e.target.value)}
                        placeholder="…or paste a custom image URL (https:// or data:)"
                        className="input flex-1 text-xs"
                    />
                    <button
                        type="button"
                        data-testid="grabber-apply-custom"
                        disabled={!customUrl.trim()}
                        onClick={() => onSelect(null, customUrl.trim())}
                        className="px-3 py-1.5 bg-slate-900 text-white text-xs rounded-md hover:bg-slate-700 disabled:opacity-40"
                    >Use this URL</button>
                </div>
            </div>
        </div>
    );
}
